import { useState, useEffect, useRef, useCallback } from 'react';

interface UseTimerOptions {
  initialTime: number; // in seconds
  onTick?: (timeRemaining: number) => void;
  onComplete?: () => void;
  autoStart?: boolean;
}

interface UseTimerReturn {
  timeRemaining: number;
  isRunning: boolean;
  isPaused: boolean;
  isComplete: boolean;
  start: (startTime?: number) => void;
  pause: () => void;
  resume: () => void;
  reset: (newTime?: number) => void;
  stop: () => void;
}

/**
 * Custom hook for managing a countdown timer
 * @param options - Timer configuration options
 * @returns Timer state and control functions
 */
export const useTimer = ({
  initialTime,
  onTick,
  onComplete,
  autoStart = false,
}: UseTimerOptions): UseTimerReturn => {
  const [timeRemaining, setTimeRemaining] = useState(initialTime);
  const [isRunning, setIsRunning] = useState(autoStart);
  const [isPaused, setIsPaused] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const pausedTimeRef = useRef<number>(initialTime);

  // Clear interval helper
  const clearTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Start the timer
  const start = useCallback((startTime?: number) => {
    if (isComplete && startTime === undefined) return;

    // If a start time is provided, reset first
    if (startTime !== undefined) {
      setTimeRemaining(startTime);
      setIsComplete(false);
      pausedTimeRef.current = startTime;
    }

    setIsRunning(true);
    setIsPaused(false);
    startTimeRef.current = Date.now();
  }, [isComplete]);

  // Pause the timer
  const pause = useCallback(() => {
    if (!isRunning || isPaused) return;

    setIsPaused(true);
    setIsRunning(false);
    clearTimer();
    pausedTimeRef.current = timeRemaining;
  }, [isRunning, isPaused, timeRemaining, clearTimer]);

  // Resume the timer
  const resume = useCallback(() => {
    if (!isPaused) return;

    setIsPaused(false);
    setIsRunning(true);
    startTimeRef.current = Date.now();
  }, [isPaused]);

  // Reset the timer
  const reset = useCallback((newTime?: number) => {
    clearTimer();
    const resetTime = newTime ?? initialTime;
    setTimeRemaining(resetTime);
    setIsRunning(false);
    setIsPaused(false);
    setIsComplete(false);
    startTimeRef.current = null;
    pausedTimeRef.current = resetTime;
  }, [initialTime, clearTimer]);

  // Stop the timer completely
  const stop = useCallback(() => {
    clearTimer();
    setIsRunning(false);
    setIsPaused(false);
  }, [clearTimer]);

  // Main timer effect
  useEffect(() => {
    if (!isRunning || isPaused) {
      return;
    }

    // Start the interval
    intervalRef.current = setInterval(() => {
      const now = Date.now();
      const elapsed = Math.floor((now - (startTimeRef.current || now)) / 1000);
      const newTimeRemaining = Math.max(0, pausedTimeRef.current - elapsed);

      setTimeRemaining(newTimeRemaining);

      // Call onTick callback
      if (onTick) {
        onTick(newTimeRemaining);
      }

      // Check if timer is complete
      if (newTimeRemaining <= 0) {
        clearTimer();
        setIsRunning(false);
        setIsComplete(true);

        if (onComplete) {
          onComplete();
        }
      }
    }, 100); // Update every 100ms for smooth countdown

    // Cleanup on unmount or when dependencies change
    return () => {
      clearTimer();
    };
  }, [isRunning, isPaused, onTick, onComplete, clearTimer]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimer();
    };
  }, [clearTimer]);

  return {
    timeRemaining,
    isRunning,
    isPaused,
    isComplete,
    start,
    pause,
    resume,
    reset,
    stop,
  };
};
